/**
 * Servicio para gestión de borradores de contratación
 * Fecha: Enero 2025
 * 
 * Integración con endpoints del backend:
 * - GET /hiring/hire-candidate/:candidateId/draft
 * - PATCH /hiring/hire-candidate/:candidateId/draft
 * - DELETE /hiring/hire-candidate/:candidateId/draft
 */

import { apiRequest, API_URL } from '@/shared/lib/apiUtils';
import type { HireEmployeeDto } from '@/types/employee.types';

export interface HireDraft {
  id: string;
  candidateId: string;
  draftData: Partial<HireEmployeeDto>;
  completenessPercentage: number;
  missingFields: string[];
  updatedAt: string;
  expiresAt?: string;
}

/**
 * Servicio de borradores de contratación
 */
export const hireDraftService = {
  /**
   * Obtener borrador guardado para un candidato
   */
  getDraft: async (candidateId: string): Promise<HireDraft | null> => {
    try {
      return await apiRequest<HireDraft>(
        `${API_URL}/hiring/hire-candidate/${candidateId}/draft`
      );
    } catch (errorCrudo: unknown) {
      const error = errorCrudo as {
        message?: string;
        statusCode?: number;
        status?: number;
        error?: string;
        response?: { status?: number; data?: { message?: string } };
      };
      if (error.status === 404 || error.message?.includes('404')) {
        return null;
      }
      throw error;
    }
  },

  /**
   * Guardar o actualizar borrador (PATCH)
   */
  saveDraft: async (
    candidateId: string,
    data: Partial<HireEmployeeDto>
  ): Promise<HireDraft> => {
    try {
      return await apiRequest<HireDraft>(
        `${API_URL}/hiring/hire-candidate/${candidateId}/draft`,
        {
          method: 'PATCH',
          body: JSON.stringify(data),
        }
      );
    } catch (errorCrudo: unknown) {
      const error = errorCrudo as {
        message?: string;
        statusCode?: number;
        status?: number;
        error?: string;
        response?: { status?: number; data?: { message?: string } };
      };
      // Capturar y re-lanzar el error con información completa
      console.error('Error en saveDraft:', error);
      throw {
        message: error.message || 'Error al guardar borrador',
        response: {
          data: error.response?.data || error,
          status: error.status || error.response?.status,
        },
      };
    }
  },

  /**
   * Eliminar borrador
   */
  deleteDraft: async (candidateId: string): Promise<void> => {
    await apiRequest<void>(
      `${API_URL}/hiring/hire-candidate/${candidateId}/draft`,
      {
        method: 'DELETE',
      }
    );
  },
};
